/**
 * Composer Fast (Dax, 2026-10-31): an edit cut on keyframes, its picture copied rather than re-encoded. Pure, so the
 * grid, the snapping and the split are tested. A keyframe is a complete picture and the only place a copied cut can
 * start. A cut can also only stop cleanly at one, and when that keyframe has leading frames (HEVC CRA) the cut shows
 * up to the first of them, not up to the keyframe, so each keyframe carries both times.
 */
/** Keyframes this far apart or closer are ready to cut. */
export const KEYFRAME_READY_MAX_MS = 2000;
/** Up to this far apart still cuts, coarsely; further apart, the clip wants preparing first. */
export const KEYFRAME_COARSE_MAX_MS = 5000;
/** Real files run a little over a round interval, so a 2 s file reads as 2 s. */
const KEYFRAME_SPACING_TOLERANCE = 1.1;
/** How near a part's end an edge may fall and still mean the end of the part: under a frame at 24 fps. */
const PART_END_TOLERANCE_MS = 40;
/**
 * A source longer than this is not decoded in the browser to level it: 10 minutes of stereo is around 230 MB in memory, and
 * a fast edit's whole point is large files. Its own sound simply is not levelled, and the master holds.
 */
export const LEVEL_DECODE_MAX_MS = 10 * 60 * 1000;
/** A slot can be split into this many parts. */
export const FAST_SPLIT_PARTS = [2, 3, 4, 5];
/** The file a part is cut from: its prepared copy, the original it arrived as, or the part itself. */
export const cutSourceOf = (p) => p.preparedKey ?? p.originalKey ?? p.key;
/** How wide against tall a clip must be to count as landscape, or upright the other way round; between them it is square. */
const SQUARE_BAND = 1.25;
/** The format nearest a clip's own shape: a fast edit stays the shape it was filmed. */
export function formatForShape(width, height) {
    if (!width || !height)
        return '9:16';
    const ratio = width / height;
    return ratio >= SQUARE_BAND ? '16:9' : ratio <= 1 / SQUARE_BAND ? '9:16' : '1:1';
}
/** What one part's keyframes say about cutting it. */
export function keyframeGrade(k) {
    if (k.openH264)
        return 'prepare';
    if (k.keyCount <= 1 || k.spacingMs === null)
        return k.durationMs <= KEYFRAME_COARSE_MAX_MS ? 'coarse' : 'prepare';
    if (k.spacingMs <= KEYFRAME_READY_MAX_MS * KEYFRAME_SPACING_TOLERANCE)
        return 'ready';
    return k.spacingMs <= KEYFRAME_COARSE_MAX_MS * KEYFRAME_SPACING_TOLERANCE ? 'coarse' : 'prepare';
}
/** The middle gap between keyframes, in ms; null with fewer than two. */
export function keySpacingMs(keys) {
    if (keys.length < 2)
        return null;
    const gaps = keys.slice(1).map((k, i) => k[0] - keys[i][0]).sort((a, b) => a - b);
    return gaps[gaps.length >> 1];
}
/** Where each part sits on its clip's timeline: where the analysis placed it, or one after another until then. */
export function partSpans(parts) {
    let at = 0;
    return parts.map((p) => {
        const startMs = p.offsetMs ?? at;
        const endMs = startMs + (p.durationMs ?? 0);
        at = endMs;
        return { partId: p.id, startMs, endMs };
    });
}
export function cutGrid(spans, keysByPart) {
    const points = spans.flatMap((sp) => (keysByPart.get(sp.partId) ?? []).map(([t, e]) => ({ startMs: sp.startMs + t, endMs: sp.startMs + e })));
    return { points, clipEndMs: spans.at(-1)?.endMs ?? 0 };
}
function nearest(list, at, ms) {
    let best = null;
    for (const x of list)
        if (best === null || Math.abs(at(x) - ms) < Math.abs(at(best) - ms))
            best = x;
    return best;
}
/** Every place a cut starting at `afterMs` could stop: the keyframes beyond it, and the end of the clip. */
function stopsAfter(grid, afterMs) {
    const stops = grid.points.map((p) => p.endMs).filter((e) => e > afterMs);
    if (grid.clipEndMs > afterMs)
        stops.push(grid.clipEndMs);
    return stops;
}
/** A range onto the keyframes: the nearest start, then the nearest stop after it. Unchanged with no keyframes known. */
export function snapRange(grid, range) {
    const start = nearest(grid.points, (p) => p.startMs, range.inMs);
    if (!start)
        return { inMs: range.inMs, outMs: range.outMs };
    const stop = nearest(stopsAfter(grid, start.startMs), (e) => e, range.outMs);
    return { inMs: start.startMs, outMs: stop ?? range.outMs };
}
/** One handle onto the keyframes as it is dragged, always keeping a stretch between it and the other handle. */
export function snapEdge(grid, which, ms, otherMs) {
    if (which === 'out')
        return nearest(stopsAfter(grid, otherMs), (e) => e, ms) ?? ms;
    const starts = grid.points.map((p) => p.startMs).filter((t) => t < otherMs);
    return nearest(starts, (t) => t, ms) ?? ms;
}
/**
 * A range split into `parts` even segments, each ending where the next begins, on keyframes. Fewer come back when
 * the range holds too few keyframes to make that many.
 */
export function splitEvenly(grid, range, parts) {
    const snapped = snapRange(grid, range);
    const inside = grid.points.filter((p) => p.startMs > snapped.inMs && p.startMs < snapped.outMs);
    const cuts = [];
    for (let i = 1; i < parts; i++) {
        const target = snapped.inMs + ((snapped.outMs - snapped.inMs) * i) / parts;
        const free = inside.filter((p) => !cuts.includes(p) && (cuts.length === 0 || p.startMs > cuts.at(-1).startMs));
        const pick = nearest(free, (p) => p.startMs, target);
        if (pick)
            cuts.push(pick);
    }
    const out = [];
    let from = snapped.inMs;
    for (const c of cuts) {
        out.push({ inMs: from, outMs: c.endMs });
        from = c.startMs;
    }
    out.push({ inMs: from, outMs: snapped.outMs });
    return out.filter((r) => r.outMs > r.inMs);
}
/**
 * A slot's range as the pieces to copy, one per part it touches. Null when a part it touches has no keyframes read
 * yet. `packets` stops the piece at the keyframe it ends on, or at the end of the part.
 */
export function planPieces(range, spans, parts) {
    const pieces = [];
    for (const sp of spans) {
        if (sp.endMs <= range.inMs || sp.startMs >= range.outMs)
            continue;
        const part = parts.get(sp.partId);
        if (!part?.keys.length)
            return null;
        const localIn = Math.max(0, range.inMs - sp.startMs);
        const start = nearest(part.keys, (k) => k[0], localIn);
        const runsOn = range.outMs >= sp.endMs - PART_END_TOLERANCE_MS;
        const localOut = runsOn ? sp.endMs - sp.startMs : range.outMs - sp.startMs;
        const stop = runsOn ? null : nearest(part.keys.filter((k) => k[2] > start[2]), (k) => k[1], localOut);
        pieces.push({
            partId: sp.partId, startMs: start[0], packets: (stop ? stop[2] : part.packets) - start[2], firstBytes: start[3],
            localInMs: start[0], localOutMs: stop ? stop[1] : localOut,
        });
    }
    return pieces;
}
/** Why a composition cannot have its picture copied, in a person's words; null when it can. */
export function copyBlocker(d) {
    if (d.hasText)
        return 'Text is burned into the picture';
    if (d.anyUnread)
        return 'A clip\'s keyframes could not be read';
    if (d.anyOpenH264)
        return 'A clip\'s keyframes are not clean places to start';
    if (new Set(d.joinKeys).size > 1 || d.joinKeys.includes(null))
        return 'The clips were not filmed with the same settings';
    return null;
}
//# sourceMappingURL=fast.js.map